import { TFunction } from 'i18next';
import { PriceDetails } from '../../../models/Entities/PriceList/PriceDetail';
import { NewPriceDetails } from '../../../models/Entities/PriceList/NewPriceDetail';
import { AppState } from '../../../redux/reducers';
import * as PriceStore from '../../../redux/middleware/PriceDetailsMiddleware';

// ── Own Props ──────────────────────────────────────────────────────────────────

export interface OwnProps {
  isOpen: boolean;
  openPanel: () => void;
  dismissPanel: () => void;
  PriceListId: number;
  PriceDetails: PriceDetails | undefined;
  showPrice: boolean;
  showPreScreeningPrice: boolean;
  showRemotePrice: boolean;
  showFailPrice: boolean;
  showRandomPrice: boolean;
  showAdditionalPaymentPrice: boolean;
}

// ── Connected Props ────────────────────────────────────────────────────────────

interface ConnectedProps {
  PriceDetailsState: AppState['PriceDetails'];
}

// ── Dispatch Props ─────────────────────────────────────────────────────────────

export interface DispatchProps {
  SavePrice: (...args: Parameters<typeof PriceStore.actionCreators.SavePrice>) => void;
  AddNewPrice: (...args: Parameters<typeof PriceStore.actionCreators.AddNewPrice>) => void;
}

// ── Merged Props ───────────────────────────────────────────────────────────────

export type Props = OwnProps & ConnectedProps & DispatchProps;

// ── Form types ─────────────────────────────────────────────────────────────────

export interface FormValidation {
  success: boolean;
  errors: string[];
}

export interface PriceFormState {
  itemReferredId: number | undefined;
  itemReferredName: string | undefined;
  price: number | undefined;
  remotePrice: number;
  failPrice: number;
  preScreeningPrice: number;
  randomPrice: number;
  additionalPaymentPrice: number;
  currency: string | undefined;
  isEnabled: boolean;
}

export const initialPriceFormState: PriceFormState = {
  itemReferredId: undefined,
  itemReferredName: undefined,
  price: undefined,
  remotePrice: 0,
  failPrice: 0,
  preScreeningPrice: 0,
  randomPrice: 0,
  additionalPaymentPrice: 0,
  currency: undefined,
  isEnabled: true
};

// ── Utilities ──────────────────────────────────────────────────────────────────

export interface CurrencyOption {
  key: string;
  text: string;
}

export const currencyOptions: CurrencyOption[] = [
  { key: 'ARS', text: '$ (Pesos Argentinos)' },
  { key: 'EUR', text: '€ (Euros)' },
  { key: 'USD', text: 'U$D (Dollars)' },
  { key: 'CL', text: 'CL (Pesos Chilenos)' },
  { key: 'R', text: 'R (Reales)' },
  { key: 'MXN', text: '$MX (Pesos Mexicanos)' }
];

export const formStateFromPriceDetails = (details: PriceDetails): PriceFormState => ({
  itemReferredId: details.itemReferredId,
  itemReferredName: details.itemReferredName,
  price: details.price,
  remotePrice: details.remotePrice,
  failPrice: details.failPrice,
  preScreeningPrice: details.preScreeningPrice,
  randomPrice: details.randomPrice,
  additionalPaymentPrice: details.additionalPaymentPrice,
  currency: details.currency,
  isEnabled: details.isEnabled
});

export const validateForm = (state: PriceFormState, t: TFunction): FormValidation => {
  const errors: string[] = [];

  if (state.itemReferredId === undefined) {
    errors.push(t('priceListItemForm.formValidation.selectServiceError'));
  }

  if (state.price === undefined) {
    errors.push(t('priceListItemForm.formValidation.informPriceError'));
  }

  return { success: errors.length === 0, errors };
};

export const buildPricePayload = (
  state: PriceFormState,
  existing: PriceDetails,
  priceListId: number
): PriceDetails => ({
  id: existing.id,
  itemReferredId: state.itemReferredId!,
  itemReferredName: state.itemReferredName!,
  itemReferredType: existing.itemReferredType || 'MedicalService',
  price: state.price!,
  remotePrice: state.remotePrice,
  failPrice: state.failPrice,
  preScreeningPrice: state.preScreeningPrice,
  randomPrice: state.randomPrice,
  additionalPaymentPrice: state.additionalPaymentPrice,
  currency: state.currency!,
  pricelistEntityId: priceListId,
  isEnabled: state.isEnabled,
  isChanged: existing.isChanged
});

export const buildNewPricePayload = (
  state: PriceFormState,
  priceListId: number
): NewPriceDetails => ({
  itemReferredId: state.itemReferredId!,
  itemReferredName: state.itemReferredName!,
  itemReferredType: 'MedicalService',
  price: state.price!,
  remotePrice: state.remotePrice,
  failPrice: state.failPrice,
  preScreeningPrice: state.preScreeningPrice,
  randomPrice: state.randomPrice,
  additionalPaymentPrice: state.additionalPaymentPrice,
  currency: state.currency!,
  pricelistEntityId: priceListId
});

// ── Redux wiring ───────────────────────────────────────────────────────────────

export const mapStateToProps = (state: AppState) => ({
  PriceDetailsState: state.PriceDetails
});

export const mapDispatchToProps: DispatchProps = {
  SavePrice: PriceStore.actionCreators.SavePrice,
  AddNewPrice: PriceStore.actionCreators.AddNewPrice
};
